import React, { useEffect, useState } from 'react';
import { Search, MoreHorizontal, Edit, Trash2, Power, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ActionModal from '../components/Modals/ActionModal';
import api from "../../../services/api";
import { Pagination } from '../../../services/helper';

export default function Challenges() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, position: 'bottom' });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, challenge: null });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);

  // React Hook Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      duration_days: '',
      reward_points: '',
      posts_per_day: '',
      comments_per_day: '',
    },
  });

  // Fetch Challenges
  const { data, isLoading } = useQuery({
    queryKey: ['challenges',page, search],
    queryFn: () => api.getChallenges(page, search),
    
    enabled: true,
    keepPreviousData: true, // Smooth UX when changing pages/search
  });

  const challengeData=data?.data;
  const pagination = data?.pagination || { page: 1, totalPages: 1, hasNext: false, hasPrev: false };

  // Mutations
  const createMutation = useMutation({
    mutationFn: (payload) => api.createChallenge(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api.updateChallenge(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.deleteChallenge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      setDeleteModal({ isOpen: false, challenge: null });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => api.toggleChallengeStatus(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['challenges'] }),
  });

  const onSubmit = (formData) => {
    const payload = {
      title: formData.title,
      description: formData.description,
      duration_days: Number(formData.duration_days) || 0,
      reward_points: Number(formData.reward_points) || 0,
      posts_per_day: Number(formData.posts_per_day) || 0,
      comments_per_day: Number(formData.comments_per_day) || 0,
    };

    if (isEditMode && editingId) {
      updateMutation.mutate({ id: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (challenge) => {
    reset({
      title: challenge.title,
      description: challenge.description || '',
      duration_days: challenge.duration_days,
      reward_points: challenge.reward_points,
      posts_per_day: challenge.posts_per_day,
      comments_per_day: challenge.comments_per_day,
    });

    setIsEditMode(true);
    setEditingId(challenge.id);
    setActiveMenu(null);

    document.getElementById('create-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const resetForm = () => {
    reset({
      title: '',
      description: '',
      duration_days: '',
      reward_points: '',
      posts_per_day: '',
      comments_per_day: '',
    });
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleDelete = (challenge) => {
    setDeleteModal({ isOpen: true, challenge });
    setActiveMenu(null);
  };

  const toggleStatus = (id) => {
    toggleMutation.mutate(id);
    setActiveMenu(null);
  };

  const toggleMenu = (id, event) => {
    if (activeMenu === id) {
      setActiveMenu(null);
      return;
    }

    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const dropdownHeight = 130;
    const spaceBelow = window.innerHeight - rect.bottom;

    let top = rect.bottom + window.scrollY + 5;
    let position = 'bottom';

    if (spaceBelow < dropdownHeight) {
      top = rect.top + window.scrollY - dropdownHeight - 5;
      position = 'top';
    }

    setMenuPosition({ top, left: rect.right - 160, position });
    setActiveMenu(id);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeMenu && !e.target.closest('.dropdown-menu') && !e.target.closest('.dropdown-trigger')) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeMenu]);

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }} className="bg-gray-50">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <h1 className="text-[22px] sm:text-[28px] md:text-[33px] lg:text-[30px] 2xl:text-[30px] font-bold text-gray-900 mb-1 leading-tight">
            Challenges
          </h1>
          <p className="text-gray-500 text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]">
            Create and monitor structured challenges with daily tasks and rewards
          </p>
        </div>
      </div>

      {/* Create / Edit Form */}
      <div id="create-form" className="bg-white rounded-2xl p-6 mb-8 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[22px] font-semibold text-gray-900">
            {isEditMode ? "Edit Challenge" : "Create Challenge"}
          </h2>
          {isEditMode && (
            <button onClick={resetForm} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
              <X size={16} /> Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Title <span className="text-red-500">*</span></label>
            <input
              {...register("title", { required: "Title is required" })}
              placeholder="e.g 7-Days Reflection"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              {...register("description")}
              rows={4}
              placeholder="Describe the challenge purpose"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration (Days) <span className="text-red-500">*</span></label>
              <input
                type="number"
                {...register("duration_days", { required: "Duration is required" })}
                placeholder="7"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400"
              />
              {errors.duration_days && <p className="text-red-500 text-sm mt-1">{errors.duration_days.message}</p>}

            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Reward Points <span className="text-red-500">*</span></label>
              <input
                type="number"
                {...register("reward_points", {
                  required: "Reward Points is required",
                  min: {
                    value: 1,
                    message: "Reward Points must be at least 1"
                  },
                  valueAsNumber: true   // Ensures it's treated as number
                })}
                placeholder="350"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400"
              />
              {errors.reward_points && <p className="text-red-500 text-sm mt-1">{errors.reward_points.message}</p>}

            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Posts/Day</label>
              <input
                type="number"
                {...register("posts_per_day", {
                  required: "Posts per day is required",
                  min: {
                    value: 1,
                    message: "Posts per day must be at least 1"
                  },
                  valueAsNumber: true   // Ensures it's treated as number
                })}
                placeholder="7"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400"
              />
              {errors.posts_per_day && <p className="text-red-500 text-sm mt-1">{errors.posts_per_day.message}</p>}

            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Comments/Day</label>
              <input
                type="number"
                {...register("comments_per_day", {
                  required: "Comments Per Day is required",
                  min: {
                    value: 1,
                    message: "Comments Per Day must be at least 1"
                  },
                  valueAsNumber: true   // Ensures it's treated as number
                })}

                placeholder="350"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400"
              />
              {errors.comments_per_day && <p className="text-red-500 text-sm mt-1">{errors.comments_per_day.message}</p>}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
              className="px-6 py-2.5 bg-[#1F3A60] hover:bg-[#172e4e] text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-60"
            >
              {isSubmitting || createMutation.isPending || updateMutation.isPending
                ? (isEditMode ? 'Updating...' : 'Creating...')
                : (isEditMode ? "Update Challenge" : "Create Challenge")}
            </button>
          </div>
        </form>
      </div>

      {/* Challenges Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-4 border-b border-gray-200">
          <span className="text-base font-semibold text-gray-700">All Challenges</span>

          <div className="relative w-full sm:w-[549px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search here..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400 bg-gray-50"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                {["Title", "Description", "Reward (Points)", "Comments/Day", "Posts/Day", "Duration (Days)", "Status", "Action"].map((h) => (
                  <th
                    key={h}
                    className={`px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap ${h === "Action" ? "text-center" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : challengeData.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">No challenges found</td></tr>
              ) : (
                challengeData.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 font-semibold">{row.title}</td>
                    <td className="px-5 py-4 max-w-[250px] truncate text-sm">{row.description}</td>
                    <td className="px-5 py-4 text-sm">{row.reward_points}</td>
                    <td className="px-5 py-4 text-sm">{row.comments_per_day}</td>
                    <td className="px-5 py-4 text-sm">{row.posts_per_day}</td>
                    <td className="px-5 py-4 text-sm">{row.duration_days}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${row.status === 'active' ? 'text-teal-600 bg-teal-50 border-teal-200' : 'text-red-500 bg-red-50 border-red-200'}`}>
                        {row.status === 'active' ? 'Active' : 'Expired'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <button onClick={(e) => toggleMenu(row.id, e)} className="dropdown-trigger">
                        <MoreHorizontal size={24} strokeWidth={2.5} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={setPage} />
      {/* Dropdown Menu */}
      {activeMenu && (
        <div className="fixed z-50 dropdown-menu" style={{ top: menuPosition.top, left: menuPosition.left }}>
          <div className="w-40 bg-white rounded-xl shadow-xl border py-1">
            {(() => {
              const challenge = challengeData.find(c => c.id === activeMenu);
              if (!challenge) return null;
              return (
                <>
                  <button onClick={() => handleEdit(challenge)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50">
                    <Edit size={14} /> Edit
                  </button>
                  <button onClick={() => toggleStatus(challenge.id)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50">
                    <Power size={14} /> {challenge.status === 'active' ? 'Expire' : 'Activate'}
                  </button>
                  <button onClick={() => handleDelete(challenge)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                    <Trash2 size={14} /> Delete
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Delete Modal */}
      <ActionModal
        isOpen={deleteModal.isOpen}
        onRequestClose={() => setDeleteModal({ isOpen: false, challenge: null })}
        onConfirm={() => deleteMutation.mutate(deleteModal.challenge.id)}
        actionType="delete"
        title={deleteModal.challenge?.title}
        itemName="Challenge"
        isLoading={deleteMutation.isPending}
        dangerColor="#DC2626"
        customDescription={`This will permanently delete "${deleteModal.challenge?.title}". This action cannot be undone.`}
      />
    </div>
  );
}